/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDateRange from 'components/MDDateRange';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import SaleOrderList from 'layouts/sale_order/SaleOrderList';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import stepperName from './ReturnProductStatus';
import ReturnProductStepper from './ReturnProductStepper';
import ReturnProductList from './ReturnProductList';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  shift_code: '',
  cashier_name: '',
  branch_name: '',
  status: 'edit',
  created_at: '',
  updated_at: '',
  total: '',
  so_code: '',
  date_record: Func.dateNow(),
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL',
  period: 'ALL',
  branch: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'scan', name: stepperName.scan.name },
  { id: 'photo', name: stepperName.photo.name },
  { id: 'request', name: stepperName.request.name },
  { id: 'approve', name: stepperName.approve.name },
  { id: 'reject', name: stepperName.reject.name },
  { id: 'process', name: stepperName.process.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'cancel', name: stepperName.cancel.name },
];

function ReturnProduct() {
  const mod = 'return_product';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [stepper, setStepper] = useState({});
  const [image, setImage] = useState([]);
  const [photo, setPhoto] = useState({});
  const [modalPhoto, setModalPhoto] = useState(false);
  const [tab, setTab] = useState(0);
  const [cboBranch, setCboBranch] = useState([]);
  const [soId, setSoId] = useState('');
  const listRef = useRef();

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.so_id', label: 'บิลขาย', align: 'center' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left' },
    { id: 'cs.code', label: 'รอบ', align: 'center' },
    { id: 'ch.name', label: 'เครื่อง POS', align: 'center' },
    { id: 'tb.nettotal', label: 'จำนวนเงิน', align: 'right' },
    { id: 'tb.created_at', label: 'วันที่', align: 'center' },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      branch: data.branch,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setNavigate(result.navigate);
        setStepper(result.stepper);
        setImage(result.image);
        setSoId(result.data.so_id);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.car_id, 1)) return;
    if (!Func.verifyLength(attr.driver_id, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      remark: attr.remark,
      car_id: attr.car_id,
      driver_id: attr.driver_id,
      date_record: attr.date_record,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          remark: attr.remark,
          car_id: attr.car_id,
          driver_id: attr.driver_id,
          date_record: attr.date_record,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            open();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          status: status,
        }).then((result) => {
          if (result.status === 'ok') {
            open();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const del = () => {
    setLoading(true);
    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboBranch(result.branch);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnSaleOrder = (item) => {
    if (item.so_code === '') return null;

    return (
      <NavLink to={`/sale_order/${item.so_id}`}>
        <MDButton
          variant='text'
          color='warning'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.so_code}
        </MDButton>
      </NavLink>
    );
  };

  const statusDisplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: '14px',
          padding: '0 10px',
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: 'nowrap',
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>{t('element.show_all')}</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell
                        align='center'
                        onClick={() => {
                          setPhoto(row);
                          setModalPhoto(true);
                        }}
                      >
                        {Func.imageDisplay(row)}
                      </TableCell>
                      <TableCell align='center' width={160}>
                        {statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='center'>{btnSaleOrder(row)}</TableCell>
                      <TableCell align='left'>{row.branch_name}</TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.shift_code}
                      </TableCell>
                      <TableCell align='left'>{row.cashier_name}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.nettotal)}</TableCell>
                      <TableCell align='center' className='nowarp'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='วันที่ขาย'
            value={Func.dateDisplay(attr.date_record)}
            variant='standard'
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='สาขา'
            value={attr.branch_name}
            variant='standard'
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='เลขที่บิลขาย'
            value={attr.so_code}
            variant='standard'
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='รอบการขาย'
            value={attr.shift_code}
            variant='standard'
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='เครื่องที่ขาย'
            value={attr.cashier_name}
            variant='standard'
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'cancel';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={ReturnProductStepper}>
            {action(key) ? (
              <MDButton
                variant='gradient'
                color={stepperName[key].color}
                size='small'
                onClick={() => approve(key)}
              >
                <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === 'scan') return 2;
      if (attr.status === 'photo') return 3;
      if (attr.status === 'request') return 4;
      if (attr.status === 'approve') return 5;
      if (attr.status === 'reject') return 6;
      if (attr.status === 'process') return 7;
      if (attr.status === 'success') return 8;
      if (attr.status === 'cancel') return 9;

      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('scan')}
          {stepLevel('photo')}
          {stepLevel('request')}
          {stepLevel('approve')}
          {stepLevel('reject')}
          {stepLevel('process')}
          {stepLevel('success')}
          {stepLevel('cancel')}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          ใบรับเข้า
        </MDTypography>
        {renderInfo()}
      </Paper>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2}>
        {image.map((item) => (
          <Grid item xs={12} sm={12} md={6} lg={3} key={item.id}>
            <MDBox className='card-img'>
              <CardHeader
                title={`Photo ID : #${item.id}`}
                subheader={Func.dateTimeDisplay(item.created_at)}
              />
              <CardMedia
                sx={{ height: 300, mt: 0 }}
                image={item.image}
                title={item.id}
                onClick={() => {
                  setPhoto(item);
                  setModalPhoto(true);
                }}
              />
              <CardContent>
                <MDTypography variant='body2'>สร้างโดย : {item.user_name}</MDTypography>
              </CardContent>
            </MDBox>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderButton = () => {
    if (attr.status !== 'edit') return null;

    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        <MDButton variant='gradient' color='success' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      </MDBox>
    );
  };

  const renderLocation = (key) => {
    if (stepper[key] === undefined) return null;

    const latitude = stepper[key].latitude.toString();
    const longitude = stepper[key].longitude.toString();
    const linkmap = stepper[key].linkmap.toString();

    if (latitude === '') return null;

    return (
      <MDBox className='card' p={2}>
        <Grid container>
          <Grid item xs={6}>
            <MDTypography variant='h5' mb={3}>
              {stepperName[key].name}
            </MDTypography>
          </Grid>
          <Grid item xs={6}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='text' color='dark' size='small' href={linkmap} target='_blank'>
                <i className='fas fa-map-marker-alt' /> &nbsp; MAP
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <Iframe
              url={`https://www.google.com/maps/embed?pb=!1m10!1m8!1m3!1d18415.335832994566!2d${longitude}!3d${latitude}!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sth!2sth!4v1736686809718!5m2!1sth!2sth`}
              width='100%'
              height='300'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    aria-label='tabs-addedit'
                    sx={{ overflowX: 'scroll' }}
                  >
                    <Tab label={t(`element.info`)} />
                    {image.length > 0 ? <Tab label='ภาพประกอบ' /> : null}
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container mb={3} spacing={2}>
                    <Grid item xs={12} xl={6}>
                      <MDTypography variant='h3'>#{attr.code}</MDTypography>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderInfoEdit()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderLocation('counting')}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderLocation('photo')}
                    </Grid>
                    <Grid item xs={12} xl={12}>
                      <MDBox p={2} className='card'>
                        <ReturnProductList parent={attr} />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} xl={12}>
                      <SaleOrderList parentId={soId} ref={listRef} />
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  {renderImage()}
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {Func.dateTimeDisplay(photo.created_at)}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.pos`), t(`menu.return_product`)]} />
      {renderPage()}
      {renderAdd()}
      {renderDelete()}
      {renderPhoto()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default ReturnProduct;
